import {CircularProgress, Skeleton, TableCell, TableRow} from "@mui/material";
import axios from "axios";
import * as cronParser from "cron-parser";
import * as React from "react";
import cookie from "react-cookies";
import {Config} from "../config/config";
import {unlockCurrency} from "./constants";
import { Buffer } from "buffer";

export const unsuccessfulRequest = (error = {}) => ({success: false, ...error});

export const successRequest = (data = {}) =>
    ({success: true, serverTime: Date.now(), ...data});

const optionsPriorityCheck = (index) => index < 0 ? 100 : index;

export const setGlobalVar = (key, value) => localStorage.setItem(key, JSON.stringify(value));

export const clearGlobalVar = (key) => localStorage.removeItem(key);

export function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const getGlobalVar = (key) => {
    const data = localStorage.getItem(key);

    if (!data) {
        return [];
    }

    return JSON.parse(data);
};

export const getItemParam = (itemId, param) => {
    const itemsConfig = getGlobalVar("ITEMS");
    const item = itemsConfig.find(obj => obj.id === itemId);
    return item ? item[param] : "";
};

export namespace Helpers {
    export function isProd() {
        return Config.getInstance.environment === "Prod";
    }

    export function isStaging() {
        return Config.getInstance.environment === "Staging";
    }

    export function parseCron(value: string) {
        const interval = cronParser.parseExpression(value, {
            utc: true,
            tz: "UTC"
        });

        interval.reset(new Date().toISOString());

        return interval;
    };

    export function generateAutocompleteOption(amount: number) {
        const list = [];

        for (let i = 1; i <= amount; i++) {
            list.push({code: i, label: i});
        }

        return list;
    }

    export function getISOStringWithoutSecsAndMillisecs1(date) {
        const dateAndTime = new Date(date).toISOString().split("T")
        const time = dateAndTime[1].split(":")

        return dateAndTime[0] + "T" + time[0] + ":" + time[1]
    }

    export function updateObject(data, path, value) {
        const pathArray = path.split(".");
        let pointer = data; // points to the current nested object

        for (let i = 0, len = pathArray.length; i < len; i++) {
            const localPath = pathArray[i];

            if (pointer.hasOwnProperty(localPath)) {
                if (i === len - 1) {
                    pointer[localPath] = value;
                } else {
                    pointer = pointer[localPath];
                }
            } else {
                throw new Error("Something went wrong");
            }
        }
    }

    export function timeLeft(target: string | Date) {
        // If variable is not date object
        if (Object.prototype.toString.call(target) !== "[object Date]") {
            target = new Date(target);
        }

        return Math.max(Math.floor(((target as Date).getTime() - Date.now()) / 1000), 0);
    }

    export function uniqueArrayByKey(arr, field) {
        return arr.reduce((acc, obj) => {
            const exist = acc.find(data => obj[field] === data[field]);

            if (!exist) {
                acc.push(obj);
            }

            return acc;
        }, []);
    }

    export function conditionPrint(showDefault: boolean, value: any, defaultValue: any) {
        if (showDefault) {
            return defaultValue;
        }

        return value;
    }

    export function documentTitle(pageName: string) {
        return `${Config.getInstance.getSettingByKey("SITE_NAME")} | ${pageName || "Dashboard"}`
    }

    const transOperators = ({
        translation = "",
        item = "",
        values = [],
        parseIndex = false,
        indexChar = "parenthesis"
    }) => {
        const options = item.split("|");
        const placeholderKey = options.shift();
        let replaceItem: string = values[parseIndex ? +placeholderKey : placeholderKey];

        // If have more than one option
        if (options.length > 0) {
            // List with option priorities
            const optionsPriorities = ["translate", "upper", "lower"];
            // Other options
            options.sort((a, b) => (
                optionsPriorityCheck(optionsPriorities.indexOf(a)) - optionsPriorityCheck(optionsPriorities.indexOf(b)))
            ).forEach(option => {
                switch (option) {
                    case "translate":
                        replaceItem = getLocalizationName(replaceItem);
                        break;
                    case "upper":
                        replaceItem = replaceItem.toUpperCase();
                        break;
                    case "lower":
                        replaceItem = replaceItem.toLowerCase();
                        break;
                    case "bold":
                        replaceItem = `<b>${replaceItem}</b>`;
                        break;
                    case "underline":
                        replaceItem = `<u>${replaceItem}</u>`;
                        break;
                    case "italic":
                        replaceItem = `<i>${replaceItem}</i>`;
                        break;
                    case "color:yellow":
                        replaceItem = `<color=#FFD700>${replaceItem}</color>`;
                        break;
                    case "color:white":
                        replaceItem = `<color=#FFFFFF>${replaceItem}</color>`;
                        break;
                    case "color:red":
                        replaceItem = `<color=#ff0b07>${replaceItem}</color>`;
                        break;
                    case "color:blue":
                        replaceItem = `<color=#1424ff>${replaceItem}</color>`;
                        break;
                    case "color:green":
                        replaceItem = `<color=#10bb0f>${replaceItem}</color>`;
                        break;
                    default:
                }
            });
        }

        if (parseIndex) {
            return translation.replace(indexChar === "parenthesis" ? `(${item})` : `{${item}}`, replaceItem);
        }

        return translation.replace(`[${item}]`, replaceItem);
    }

    export const renderSkeletonRows = (rows = 1, skeletonHeight = 20) =>
        Array.from({ length: rows }, (_, index) => (
            <Skeleton key={`skeleton-row-${index}`} height={skeletonHeight} width="100%" />
        ))

    export const getLocalizationName = (localizationKey: any, values: any = []) => {
        if (typeof localizationKey !== "string") {
            localizationKey = localizationKey?.ENG || "";
        }

        const localization = getGlobalVar("LOCALIZATION");
        const translationConfig = localization.find(item => item.key === localizationKey);

        if (!translationConfig) {
            return localizationKey;
        }

        const value = translationConfig.value;
        const searchArray = Array.isArray(values);
        const searchPattern = searchArray ? /(?<=\(|{)[^\]\[\r\n]*(?=\)|})+/g : /(?<=\[)[^\]\[\r\n]*(?=\])+/g;

        // If values is object then search by square brackets
        return (String(value).match(searchPattern) || []).reduce((translation, item) => transOperators({
            translation,
            item,
            values,
            parseIndex: searchArray,
            ...(searchArray && {
                indexChar: String(value).includes("{") ? "brace" : "parenthesis",
            })
        }), value);
    };

    // Get a session token
    export const getSessionId = () => cookie.load("brl-session-token");

    export const getCurrentApplication = () => cookie.load("brl-target-app");

    export const formatTimeAgo = (date) => {
        const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);

        let interval = Math.floor(seconds / 31536000);

        if (interval > 1) {
            return formatDateWithTime(date);
        }
        interval = Math.floor(seconds / 2592000);
        if (interval > 1) {
            return formatDateWithTime(date);
        }
        interval = Math.floor(seconds / 86400);
        if (interval > 1) {
            return `${interval} days ago`;
        }
        interval = Math.floor(seconds / 3600);
        if (interval > 1) {
            return `${interval} hours ago`;
        }
        interval = Math.floor(seconds / 60);
        if (interval > 1) {
            return `${interval} minutes ago`;
        }
        return `${Math.floor(seconds)} seconds ago`;

    };

    export const formatDateString = (date) => {
        // YYYY/MM/DD-HH:MM:SS
        return formatDateWithTime(date, true);
    }

    export const formatDateWithTime = (date, setSeconds = false) => {
        const dateObj = new Date(date);
        const diff = dateObj.getTimezoneOffset() * 60 * 1000;
        const newDate = new Date(dateObj.getTime() + diff);
        const offset = dateObj.getTimezoneOffset() / 60;
        const hours = dateObj.getUTCHours();

        //newDate.setHours(hours - offset);
        newDate.setUTCHours(hours - offset);

        const day = padLeadingZero(newDate.getUTCDate());
        const month = padLeadingZero(newDate.getUTCMonth() + 1);
        const year = newDate.getUTCFullYear();
        const hour = padLeadingZero(newDate.getUTCHours());
        const minute = padLeadingZero(newDate.getUTCMinutes());
        const seconds = padLeadingZero(newDate.getUTCSeconds());

        if (setSeconds) {
            return `${day}.${month}.${year} ${hour}:${minute}:${seconds}`;
        }

        return `${day}.${month}.${year} ${hour}:${minute}`;
    };

    export const formatISODateWithTime = (origin) => {
        const dateObj = new Date(origin);
        const newDate = new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60 * 1000);
        const offset = dateObj.getTimezoneOffset() / 60;
        const offsetHours = dateObj.getHours();

        newDate.setHours(offsetHours - offset);

        const [date, time] = newDate.toISOString().split("T");
        const [hours, minutes] = time.split(":");

        return `${date}T${hours}:${minutes}`;
    };

    export const formatDateTime = (date, setSeconds = false) => {
        const dateObj = new Date(date);
        const newDate = new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60 * 1000);
        const offset = dateObj.getTimezoneOffset() / 60;
        const hours = dateObj.getHours();

        newDate.setHours(hours - offset);

        const hour = padLeadingZero(newDate.getUTCHours());
        const minute = padLeadingZero(newDate.getUTCMinutes());
        const seconds = padLeadingZero(newDate.getUTCSeconds());

        if (setSeconds) {
            return `${hour}:${minute}:${seconds}`;
        }

        return `${hour}:${minute}`;
    };

    export const getUserList = () => axios.get(`${Config.getInstance.apiUrl}/api/users`, {
        headers: {Authorization: getSessionId()},
    }).then((result) => {
        if (result.status !== 200) {
            return [];
        }
        return result.data.data;
    }).catch(() => []);

    export const a11yProps = (index: any) => ({
        "id": `wrapped-tab-${index}`,
        "aria-controls": `wrapped-tabpanel-${index}`,
    });

    export const padLeadingZero = (n) => (n < 10) ? `0${n}` : n;

    export const fullUrl = (path) => {
        if (path === "/") {
            return "/bundle/";
        }

        if (path[0] === "/") {
            path = path.substr(1);
        }

        return `/bundle/${path}`;
    };

    export const chestPriceLabel = (key) => {
        const object = unlockCurrency.find(currency => currency.id === key);
        return object ? object.label : "";
    };

    export const pushURL = (path) => {
        const newUrl = `${window.location.protocol}//${window.location.host}/${path}`;
        window.history.pushState({path: newUrl}, "", newUrl);
    };

    export const onLoginPage = () => {
        const path = window.location.pathname.split("/").filter(pathName => pathName.length > 0);
        return path[0] === "login";
    };

    export const redirectIfPossible = () => {
        const url = new URL(window.location.href);
        const searchParams = new URLSearchParams(url.search);

        if (searchParams.get("redirect") && (searchParams.get("redirect") || "").length > 0) {
            return searchParams.get("redirect");
        }

        return null;
    };

    export const ordinalSuffix = (number) => {
        const j = number % 10;
        const k = number % 100;

        if (j === 1 && k !== 11) {
            return number + "st";
        }

        if (j === 2 && k !== 12) {
            return number + "nd";
        }

        if (j === 3 && k !== 13) {
            return number + "rd";
        }

        return number + "th";
    }

    export const redirectLoginPage = (path = "") => {
        const redirect = path !== "/" && path !== "" ? `?redirect=${path}` : "";
        const newUrl = `${window.location.protocol}//${window.location.host}/login${redirect}`;
        window.history.pushState({path: newUrl}, "", newUrl);
    };

    export const capitalizeFirstLetter = (string: string) => {
        if (!string || string.length === 0 || typeof string !== "string") {
            return string;
        }

        return (string.charAt(0).toUpperCase() + string.slice(1)).replace(/_/g, " ");
    };

    export const flatten = (arr) =>
        arr.reduce((flat, toFlatten) =>
            flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten), []);

    export const fancyTimeFormat = (time, data = {} as {
        hideDays?: boolean;
        hideHours?: boolean;
        hideMinutes?: boolean;
        hideSeconds?: boolean;
    }) => {
        const seconds = Number(time);

        if (!seconds) {
            return "0s";
        }

        const d = Math.floor(seconds / (3600 * 24));
        const h = Math.floor(seconds % (3600 * 24) / 3600);
        const m = Math.floor(seconds % 3600 / 60);
        const s = Math.floor(seconds % 60);

        const dDisplay = d > 0 && !data.hideDays ? d + "d " : "";
        const hDisplay = h > 0 && !data.hideHours ? h + "h " : "";
        const mDisplay = m > 0 && !data.hideMinutes ? m + "m " : "";
        const sDisplay = s > 0 && !data.hideSeconds ? s + "s " : "";

        return dDisplay + hDisplay + mDisplay + sDisplay;
    }

    export function stringToHslColor(str, s = 60, l = 50) {
        let hash = 0;
        for (let i = 0; i < str.length; i += 1) {
            // tslint:disable-next-line:no-bitwise
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }

        const h = hash % 360;
        return `hsl(${h}, ${s}%, ${l}%)`;
    }

    export function prettyPrintJson(obj) {
        const json = JSON.stringify(obj, null, 3)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

        const parsedJson = json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
            let cls = "number";

            if (/^"/.test(match)) {
                // tslint:disable-next-line:prefer-conditional-expression
                if (/:$/.test(match)) {
                    cls = "key";
                } else {
                    cls = "string";
                }
            } else if (/true|false/.test(match)) {
                cls = "boolean";
            } else if (/null/.test(match)) {
                cls = "null";
            }

            // tslint:disable-next-line:prefer-template
            return '<span class="' + cls + '">' + match + "</span>";
        });

        return `<pre>${parsedJson}</pre>`;
    }

    export const loadingIndicator = (text, className = "") =>
        <div>
            <div className={`loadingIndicator ${className ? className : ""}`}>
                <CircularProgress />
                <span className="initText">{text}</span>
            </div>
        </div>;

    export const formatNumber = (number) => {
        // Format 1000000 to 1,000,000
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    export const getDevAdminBasicAuth = () => {
        const GAME_ADMIN_HTTP_BASIC_USER = process.env.DEV_GAME_ADMIN_HTTP_BASIC_USER || "";
        const GAME_ADMIN_HTTP_BASIC_PASS = process.env.DEV_GAME_ADMIN_HTTP_BASIC_PASS || "";
        const base64 = Buffer.from(`${GAME_ADMIN_HTTP_BASIC_USER}:${GAME_ADMIN_HTTP_BASIC_PASS}`).toString("base64");

        return base64;
    }
}
